import Joi from 'joi';

import { TaxRateTypeEnum } from '../../../../shared/enums/tax-rate-type.enum';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { VehicleInsuranceCoverageTypeEnum } from '../../enums/vehicle-insurance-coverage-type.enum';
import { VehicleInsurancePremiumPeriodEnum } from '../../enums/vehicle-insurance-premium-period.enum';
import { VehicleInsuranceRecordCategoryEnum } from '../../enums/vehicle-insurance-record-category.enum';

/**
 * Schema for updating vehicle insurance record validation.
 */
export const updateVehicleInsuranceSchema = () =>
  Joi.object({
    id: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|vehicle-insurance-id|cannot-be-null',
      }),
      otherwise: Joi.number().required().messages({
        'any.required': 'validations|vehicle-insurance-id|required',
        'number.base': 'validations|vehicle-insurance-id|must-be-number',
      }),
    }),

    code: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|vehicle-insurance-code|cannot-be-null',
      }),
      otherwise: Joi.string().required().messages({
        'any.required': 'validations|vehicle-insurance-code|required',
        'string.base': 'validations|vehicle-insurance-code|must-be-string',
        'string.empty': 'validations|vehicle-insurance-code|cannot-be-empty',
      }),
    }),

    recordCategory: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid':
          'validations|vehicle-insurance-record-category|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(VehicleInsuranceRecordCategoryEnum))
        .required()
        .messages({
          'any.required':
            'validations|vehicle-insurance-record-category|required',
          'any.only':
            'validations|vehicle-insurance-record-category|invalid-value',
          'string.base':
            'validations|vehicle-insurance-record-category|must-be-string',
          'string.empty':
            'validations|vehicle-insurance-record-category|cannot-be-empty',
        }),
    }),

    vehicleId: Joi.when('recordCategory', {
      is: VehicleInsuranceRecordCategoryEnum.VEHICLE,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-insurance-vehicle-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|vehicle-insurance-vehicle-id|required',
          'number.base':
            'validations|vehicle-insurance-vehicle-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    supplierId: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-supplier-id|must-be-number',
    }),

    policyNumber: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-policy-number|must-be-number',
    }),

    coverNoteNumber: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-cover-note-number|must-be-number',
    }),

    insuredValue: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-insured-value|must-be-number',
    }),

    premiumPeriod: Joi.string()
      .valid(...Object.values(VehicleInsurancePremiumPeriodEnum))
      .required()
      .messages({
        'any.required': 'validations|vehicle-insurance-premium-period|required',
        'any.only':
          'validations|vehicle-insurance-premium-period|invalid-value',
        'string.base':
          'validations|vehicle-insurance-premium-period|must-be-string',
        'string.empty':
          'validations|vehicle-insurance-premium-period|cannot-be-empty',
      }),

    excessAmount: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-excess-amount|must-be-number',
    }),

    totalLossPercentage: Joi.number().min(0).max(100).allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-total-loss-percentage|must-be-number',
      'number.min':
        'validations|vehicle-insurance-total-loss-percentage|cannot-be-less-than-zero',
      'number.max':
        'validations|vehicle-insurance-total-loss-percentage|cannot-exceed-100',
    }),

    computedTotalLossAmountReceivable: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-computed-total-loss-amount-receivable|must-be-number',
    }),

    amount: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-amount|must-be-number',
    }),

    totalNet: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-total-net|must-be-number',
    }),

    totalTax: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-total-tax|must-be-number',
    }),

    totalGross: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-total-gross|must-be-number',
    }),

    coverageType: Joi.string()
      .valid(...Object.values(VehicleInsuranceCoverageTypeEnum))
      .required()
      .messages({
        'any.required': 'validations|vehicle-insurance-coverage-type|required',
        'any.only': 'validations|vehicle-insurance-coverage-type|invalid-value',
        'string.base':
          'validations|vehicle-insurance-coverage-type|must-be-string',
      }),

    coverageDescription: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-insurance-coverage-description|must-be-string',
    }),

    externalReference: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-insurance-external-reference|must-be-string',
    }),

    taxRateType: Joi.string()
      .valid(...Object.values(TaxRateTypeEnum))
      .optional()
      .empty(null)
      .messages({
        'any.only': 'validations|vehicle-insurance-tax-rate-type|invalid-value',
      }),

    taxRate: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-tax-rate|must-be-number',
    }),

    isTaxIncluded: Joi.boolean().optional().empty(null),

    startsAt: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-insurance-starts-at|cannot-be-null',
        }),
        otherwise: Joi.date().required().messages({
          'any.required': 'validations|vehicle-insurance-starts-at|required',
          'date.base':
            'validations|vehicle-insurance-starts-at|must-be-a-valid-date',
        }),
      })
      .prefs({ abortEarly: true }),

    endsAt: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-insurance-ends-at|cannot-be-null',
        }),
        otherwise: Joi.date().greater(Joi.ref('startsAt')).required().messages({
          'any.required': 'validations|vehicle-insurance-ends-at|required',
          'date.base':
            'validations|vehicle-insurance-ends-at|must-be-a-valid-date',
          'date.greater':
            'validations|vehicle-insurance-ends-at|cannot-be-before-starts-at',
        }),
      })
      .prefs({ abortEarly: true }),

    tags: createTagsSchema().messages({
      'array.base': 'validations|vehicle-insurance-tags|invalid-value',
    }),

    notes: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-insurance-notes|must-be-string',
    }),

    files: Joi.any().allow(null),
  })
    // Ignore extra fields
    .unknown();
